import { type ReactNode } from 'react';
import { ResponsiveContainer, LineChart as RLineChart, Line, AreaChart as RAreaChart, Area } from 'recharts';
import { useChartTheme } from './theme.js';

export interface SparklineProps {
  data: number[] | Array<{ value: number } & Record<string, unknown>>;
  /** Stroke + fill color. Defaults to `--rr-color-primary`. */
  color?: string;
  /** Render as area (filled). Default true. */
  withArea?: boolean;
  h?: number | string;
  w?: number | string;
  curveType?: 'linear' | 'monotone' | 'step' | 'natural';
  strokeWidth?: number;
  className?: string;
}

/**
 * Compact inline trendline: for dashboards, tables, and stat cards.
 * Accepts either raw numbers or `{ value }` rows.
 */
export function Sparkline({
  data, color, withArea = true, h = 40, w = 120,
  curveType = 'monotone', strokeWidth = 1.6, className
}: SparklineProps): ReactNode {
  const theme = useChartTheme();
  const stroke = color ?? theme.primary;
  const rows = typeof data[0] === 'number'
    ? (data as number[]).map((v) => ({ value: v }))
    : (data as Array<{ value: number }>);

  const gradId = `rr-spark-grad-${stroke.replace(/[^a-z0-9]/gi, '')}`;
  const Chart = withArea ? RAreaChart : RLineChart;
  const Series = withArea ? Area : Line;

  return (
    <div className={['rr-chart', 'rr-chart--sparkline', className].filter(Boolean).join(' ')} style={{ width: w, height: h }}>
      <ResponsiveContainer width="100%" height="100%">
        <Chart data={rows} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          {withArea ? (
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={stroke} stopOpacity={0.45} />
                <stop offset="100%" stopColor={stroke} stopOpacity={0.05} />
              </linearGradient>
            </defs>
          ) : null}
          <Series
            type={curveType}
            dataKey="value"
            stroke={stroke}
            strokeWidth={strokeWidth}
            fill={withArea ? `url(#${gradId})` : undefined}
            dot={false}
            isAnimationActive={false}
          />
        </Chart>
      </ResponsiveContainer>
    </div>
  );
}
